import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { exampleVarMap, getTemplateVars, renderTemplate } from '@/utils/templateHelp';

interface TemplateVarsReferenceProps {
  taskType: string;
}

/**
 * Collapsible reference panel listing every available template
 * variable for the given task type with example values + descriptions.
 * Embedded inside AlertFormModal above each block of template inputs.
 */
export function TemplateVarsReference({ taskType }: TemplateVarsReferenceProps) {
  const [open, setOpen] = useState(false);
  const vars = getTemplateVars(taskType);
  if (vars.length === 0) return null;
  return (
    <div className="rounded-md border border-sev-5/30 bg-sev-5/5 p-3 mb-3">
      <button
        type="button"
        className="text-sm text-foreground inline-flex items-center gap-1.5 hover:underline"
        onClick={() => { setOpen((v) => !v); }}
      >
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        Available template variables ({vars.length})
        {taskType === 'device_audit' && !open ? (
          <span className="text-muted-foreground ml-1 font-normal">
            — audit findings render their own text; these mainly affect dedupe keys.
          </span>
        ) : null}
      </button>
      {open ? (
        <div className="mt-3">
          {taskType === 'device_audit' ? (
            <p className="text-xs text-muted-foreground mb-2">
              Device-audit findings produce their own title and message in Rust; the
              title/description templates below are stored but currently ignored.
              Variables still substitute inside <code className="font-mono">dedupe_key_template</code>.
            </p>
          ) : null}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="border-b border-border-subtle">
                <tr className="text-muted-foreground uppercase tracking-wider">
                  <th className="text-left py-1.5 pr-3 font-medium">Variable</th>
                  <th className="text-left py-1.5 pr-3 font-medium">Example value</th>
                  <th className="text-left py-1.5 font-medium">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {vars.map((v) => (
                  <tr key={v.name}>
                    <td className="py-1.5 pr-3">
                      <code className="font-mono">{`{${v.name}}`}</code>
                    </td>
                    <td className="py-1.5 pr-3">
                      <code className="font-mono">{v.exampleValue}</code>
                    </td>
                    <td className="py-1.5">{v.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}

interface TemplatePreviewProps {
  template: string;
  taskType: string;
}

/**
 * Tiny one-line preview rendered directly below a template input.
 * Uses the example variable values for the given task type.
 */
export function TemplatePreview({ template, taskType }: TemplatePreviewProps) {
  const rendered = useMemo(
    () => renderTemplate(template, exampleVarMap(taskType)),
    [template, taskType],
  );
  if (template.trim() === '') return null;
  const hasUnresolved = /\{[a-z_]+\}/i.test(rendered);
  return (
    <p className="text-xs mt-1">
      <span className="text-muted-foreground">Preview: </span>
      <span className={hasUnresolved ? 'text-sev-3' : 'text-foreground'}>{rendered}</span>
      {hasUnresolved ? (
        <span
          className="ml-2 text-sev-3"
          title="At least one variable in the template is not in the catalog above; it will appear literally in the rendered output."
        >
          (unknown variable left as-is)
        </span>
      ) : null}
    </p>
  );
}
